import * as React from "react";
import {
  Card,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Paper } from "../../types/paper";

interface InlineCitationPanelProps {
  searchText: string;
  papers: Paper[];
  selectedIndex: number;
  onSelect: (paper: Paper) => void;
  onClose: () => void;
}

const useStyles = makeStyles({
  panel: {
    position: "fixed",
    bottom: "0",
    left: "0",
    right: "0",
    backgroundColor: "#ffffff",
    borderTop: "3px solid #667eea",
    boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.15)",
    maxHeight: "300px",
    overflowY: "auto",
    zIndex: 1000,
    padding: "12px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    paddingBottom: "8px",
    borderBottom: "1px solid #e3e8ef",
  },
  title: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#667eea",
  },
  searchText: {
    fontSize: "12px",
    color: "#718096",
    fontStyle: "italic",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "18px",
    cursor: "pointer",
    color: "#718096",
    padding: "4px 8px",
    "&:hover": {
      color: "#667eea",
    },
  },
  paperItem: {
    padding: "10px 12px",
    margin: "4px 0",
    cursor: "pointer",
    borderRadius: "8px",
    border: "1px solid #e3e8ef",
    backgroundColor: "#ffffff",
    transition: "all 0.2s ease",
  },
  paperItemHover: {
    padding: "10px 12px",
    margin: "4px 0",
    cursor: "pointer",
    borderRadius: "8px",
    border: "1px solid #667eea",
    backgroundColor: "#f5f7fa",
    transition: "all 0.2s ease",
  },
  paperItemSelected: {
    padding: "10px 12px",
    margin: "4px 0",
    cursor: "pointer",
    borderRadius: "8px",
    border: "2px solid #667eea",
    backgroundColor: "#f0f4ff",
    transition: "all 0.2s ease",
  },
  paperTitle: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#1a202c",
    marginBottom: "4px",
    display: "-webkit-box",
    WebkitLineClamp: "1",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  paperAuthors: {
    fontSize: "11px",
    color: "#718096",
    display: "-webkit-box",
    WebkitLineClamp: "1",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  emptyState: {
    padding: "20px",
    textAlign: "center",
    color: "#718096",
    fontSize: "12px",
  },
  hint: {
    fontSize: "11px",
    color: "#a0aec0",
    marginTop: "8px",
    padding: "8px",
    backgroundColor: "#f5f7fa",
    borderRadius: "6px",
    textAlign: "center",
  },
});

const InlineCitationPanel: React.FC<InlineCitationPanelProps> = ({
  searchText,
  papers,
  selectedIndex,
  onSelect,
  onClose,
}) => {
  const styles = useStyles();
  const selectedRef = React.useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  // Scroll selected item into view
  React.useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedIndex]);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <Text className={styles.title}>📝 Inline Citation</Text>
          {searchText && (
            <Text className={styles.searchText}> - searching: "{searchText}"</Text>
          )}
        </div>
        <button className={styles.closeButton} onClick={onClose}>
          ✕
        </button>
      </div>

      {papers.length === 0 ? (
        <div className={styles.emptyState}>
          {searchText ? `No papers found matching "${searchText}"` : "Start typing to search papers..."}
        </div>
      ) : (
        <>
          {papers.slice(0, 10).map((paper, index) => (
            <div
              key={paper.id}
              ref={index === selectedIndex ? selectedRef : null}
              className={
                index === selectedIndex 
                  ? styles.paperItemSelected 
                  : hoveredIndex === index 
                    ? styles.paperItemHover 
                    : styles.paperItem
              }
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => onSelect(paper)}
            >
              <div className={styles.paperTitle}>
                {paper.starred && "⭐ "}
                {paper.title}
              </div>
              <div className={styles.paperAuthors}>
                {paper.authors.join(", ")} {paper.year && `(${paper.year})`}
              </div>
            </div>
          ))}
          <div className={styles.hint}>
            💡 Try: @starred @2024 @recent @rating:4+ @author_name • ↑↓ to navigate • Enter to insert • Esc to close
          </div>
        </>
      )}
    </div>
  );
};

export default InlineCitationPanel;
